import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Users, UserMinus, Trash2, RefreshCw, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { getNewsletterSubscribers, getNewsletterStats, unsubscribeNewsletter, deleteNewsletterSubscriber } from "@/lib/api";

export default function NewsletterAdminPage() {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, actifs: 0, desinscrits: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const loadData = useCallback(async () => {
    try {
      const [subsRes, statsRes] = await Promise.all([
        getNewsletterSubscribers(),
        getNewsletterStats(),
      ]);
      setSubscribers(subsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUnsubscribe = async (id) => {
    try {
      await unsubscribeNewsletter(id);
      toast.success("Abonné désinscrit");
      loadData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement cet abonné ?")) return;
    try {
      await deleteNewsletterSubscriber(id);
      toast.success("Abonné supprimé");
      loadData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleExportCSV = () => {
    const filtered = getFilteredSubs();
    const csv = "Email,Statut,Date d'inscription\n" + filtered.map(s =>
      `${s.email},${s.statut},${new Date(s.created_at).toLocaleDateString("fr-FR")}`
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter_subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé");
  };

  const getFilteredSubs = () => {
    return subscribers.filter(s => {
      const matchSearch = !search || s.email.toLowerCase().includes(search.toLowerCase());
      const matchFilter = filter === "all" || s.statut === filter;
      return matchSearch && matchFilter;
    });
  };

  const filtered = getFilteredSubs();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="newsletter-admin-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        {[
          { label: "Total inscrits", value: stats.total, icon: Mail, color: "bg-blue-50 text-blue-600" },
          { label: "Actifs", value: stats.actifs, icon: Users, color: "bg-green-50 text-green-600" },
          { label: "Désinscrits", value: stats.desinscrits, icon: UserMinus, color: "bg-orange-50 text-orange-600" },
        ].map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm"
            data-testid={`newsletter-stat-${i}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-3xl font-bold text-[#004333] mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                <stat.icon className="w-6 h-6" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 mb-6 flex flex-col sm:flex-row items-center gap-4">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#C9922A] focus:ring-1 focus:ring-[#C9922A]/30 outline-none"
            data-testid="newsletter-search"
          />
        </div>
        <div className="flex items-center gap-2">
          {[
            { key: "all", label: "Tous" },
            { key: "actif", label: "Actifs" },
            { key: "désinscrit", label: "Désinscrits" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.key ? "bg-[#004333] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid={`newsletter-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExportCSV} className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200 transition-colors" data-testid="newsletter-export">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={loadData} className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors" data-testid="newsletter-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="newsletter-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Email</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Statut</th>
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Date d'inscription</th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filtered.map((sub) => (
                  <motion.tr
                    key={sub.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                    data-testid={`newsletter-row-${sub.id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#004333]/10 flex items-center justify-center text-[#004333] text-xs font-semibold">
                          {sub.email.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-gray-900">{sub.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        sub.statut === "actif"
                          ? "bg-green-100 text-green-700"
                          : "bg-orange-100 text-orange-700"
                      }`}>
                        {sub.statut === "actif" ? "Actif" : "Désinscrit"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(sub.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {sub.statut === "actif" && (
                          <button
                            onClick={() => handleUnsubscribe(sub.id)}
                            className="p-1.5 rounded-lg text-orange-500 hover:bg-orange-50 transition-colors"
                            title="Désinscrire"
                            data-testid={`newsletter-unsub-${sub.id}`}
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(sub.id)}
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                          title="Supprimer"
                          data-testid={`newsletter-delete-${sub.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Aucun abonné {filter !== "all" ? "dans ce filtre" : "pour le moment"}</p>
            <p className="text-gray-400 text-sm mt-1">Les inscriptions depuis le blog apparaîtront ici.</p>
          </div>
        )}
      </div>
    </div>
  );
}
